import { useLang } from "../contexts/LangContext";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserData } from "../contexts/UserContext";
import bgImage1 from "../assets/site-images/app-bg-1.png";
import bgImage2 from "../assets/site-images/app-bg-2.png";
import { PLATFORM_UI_CONFIG, API_CONFIG } from "../config/app.config";
import { storageService } from "../services/storage.service";
import { motion } from "framer-motion";

export default function AppContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserData();
  const [appBackground, setAppBackground] = useState<'bg1' | 'bg2' | 'white'>('bg1');
  const [banglaFont, setBanglaFont] = useState<'noto' | 'lalsabuj' | 'baloo'>('noto');

  const publicRoutes = ["/login", "/signup", "/setup-pin"];
  const showSettingsButton = !!user && !publicRoutes.includes(location.pathname);
  const isSettingsPage = location.pathname === '/settings';
  
  // Home pages: /user and /survey (exact match)
  const homePages = ["/"];
  const isHomePage = homePages.includes(location.pathname);
  
  // Show home icon only when:
  // 1. User is logged in
  // 2. Not on a public/login route
  // 3. Not on home page itself
  const showHomeIcon = !!user && !publicRoutes.includes(location.pathname) && !isHomePage;

  // Load app settings
  useEffect(() => {
    const loadSettings = async () => {
      const settings = await storageService.getSettings();
      if (settings) {
        setAppBackground(settings.appBackground || 'bg1');
        setBanglaFont(settings.banglaFont || 'noto');
      }
    };
    loadSettings();
  }, []);

  // Get platform-specific padding
  const statusBarPadding = PLATFORM_UI_CONFIG.getStatusBarPadding();
  const bottomNavPadding = PLATFORM_UI_CONFIG.getBottomNavBarPadding();

  const onHeaderClick = () => {
    // Back to home if confirmed
    if (window.confirm(t("confirmBackToHome"))) {
      window.location.href = "/";
    }
  }

  const handleSettingsClick = () => {
    if (isSettingsPage) {
      // If already on settings, go back
      navigate(-1);
    } else {
      // Navigate to settings
      navigate('/settings');
    }
  };

  // Get background image based on setting
  const getBackgroundImage = () => {
    if (appBackground === 'bg1') return bgImage1;
    if (appBackground === 'bg2') return bgImage2;
    return null; // white
  };

  // Get font class based on setting
  const getFontClass = () => {
    if (lang !== 'bn') return '';
    if (banglaFont === 'lalsabuj') return 'bn-font-lalsabuj';
    if (banglaFont === 'baloo') return 'bn-font-baloo';
    return 'bn-font'; // noto (default)
  };

  const backgroundImage = getBackgroundImage();

  return (
    <div
      className={`w-full mx-auto max-w-[720px] ${getFontClass()}`}
      style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundColor: appBackground === 'white' ? '#ffffff' : undefined,
          backgroundPosition: "center",
          backgroundSize: "600px auto",
          backgroundRepeat: "repeat-y",
          backgroundAttachment: "fixed",          
        }}
    >
      <div
        className="min-h-[100dvh] flex flex-col scroll-y-auto will-change-transform"                
      >
        {/* Header */}
        <header
          id="app-header-bar"
          className="flex flex-col grow-0 shrink-0 border-b border-white bg-white bg-opacity-50 backdrop-blur-md sticky top-0 z-20"   
        >
          <div id="status-bar-padding" className="pointer-events-none" style={{ height: statusBarPadding }}></div>
          <div className="flex items-center justify-between gap-4 px-scr h-16">
            <h1 className="app-header flex items-center text-xl min-[400px]:text-2xl font-semibold text-blue-700" onClick={onHeaderClick}>
              {/* Inline SVG with animation to avoid SVGR build issues */}
              {/* <svg xmlns="http://www.w3.org/2000/svg" xmlSpace="preserve" width="24" height="24" viewBox="0 0 92.062 92.062" className="inline-block mr-2">
                <defs>
                  <style type="text/css">{`
                    @keyframes continuous-wave {
                      0%   { transform: rotate(-5deg); }
                      50%  { transform: rotate(5deg); }
                      100% { transform: rotate(-5deg); }
                    }
                    #tail-group {
                      transform-origin: -239.57px 1328.69px;
                      animation: continuous-wave 4s ease-in-out infinite;
                    }
                  `}</style>
                </defs>
                <g transform="translate(292.511 -1275.746)">
                  <rect width="52.941" height="23.529" x="-292.511" y="1305.158" rx="5.882" ry="5.882" style={{fill:'#18bb70',fillOpacity:1,strokeWidth:'11.7647',strokeLinecap:'square'}}/>
                  <path d="M-286.629 1275.746a5.87 5.87 0 0 0-5.882 5.883v11.764a5.87 5.87 0 0 0 5.882 5.883h70.588a5.87 5.87 0 0 0 5.883-5.883v-11.764a5.87 5.87 0 0 0-5.883-5.883zm8.905 5.883a5.882 5.882 0 0 1 5.882 5.882 5.882 5.882 0 0 1-5.882 5.882 5.882 5.882 0 0 1-5.883-5.882 5.882 5.882 0 0 1 5.883-5.882z" style={{fill:'#18bb70',fillOpacity:1,strokeWidth:'11.7647',strokeLinecap:'square'}}/>
                  <rect width="35.294" height="23.529" x="-292.511" y="1334.57" rx="5.882" ry="5.882" style={{fill:'#18bb70',fillOpacity:1,strokeWidth:'11.7647',strokeLinecap:'square'}}/>
                  <g id="tail-group">
                    <path d="M-32.592 276.3c-2.77 0-5 2.23-5 5v23.253c0 2.77 2.23 5 5 5h10c2.77 0 5-2.23 5-5v-4.99a3.264 3.264 135 0 1 3.264-3.264h4.988c2.77 0 5-2.23 5-5v-10c0-2.77-2.23-5-5-5h-13.253Z" style={{fill:'#6dc3a0',fillOpacity:1,strokeWidth:'10',strokeLinecap:'square'}} transform="matrix(1.17647 0 0 1.17647 -195.344 1003.63)"/>
                  </g>
                </g>
              </svg> */}
              <span>{t("appTitle")}</span>
              {showHomeIcon && (
                <div className="flex items-center ml-2 pl-2 border-l border-gray-300 cursor-pointer text-gray-600 hover:text-blue-600">
                  <motion.div
                    className="flex items-center justify-center"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <button
                      type="button"                 
                      className=""
                      aria-label="Home"
                      title="Go to Home"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                      </svg>
                    </button>
                  </motion.div>
                </div>
              )}

            </h1>
            <div className="flex items-center gap-2">
              {showSettingsButton && (
                <button
                  type="button"
                  className={`p-2 rounded-full transition-colors ${
                    isSettingsPage ? 'text-blue-600 bg-blue-50' : 'text-gray-500'
                  }`}
                  onClick={handleSettingsClick}
                  aria-label="Settings"
                  title={isSettingsPage ? "Close Settings" : "Settings"}
                >
                  {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 text-gray-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                  </svg> */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="size-5" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9.5 14a3 3 0 1 1 0 6a3 3 0 0 1 0-6Zm5-10a3 3 0 1 0 0 6a3 3 0 0 0 0-6Z" /><path strokeLinecap="round" d="M15 16.959h7m-13-10H2m0 10h2m18-10h-2" /></g></svg>
                </button>
              )}
              {/* Help icon - shown only on tablet/desktop (min-width: 500px) */}
              {showSettingsButton && (
                <a
                  href={API_CONFIG.HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden min-[500px]:block rounded-full text-gray-500 hover:text-blue-600 transition-colors"
                  aria-label={t('settings_helpManual')}
                  title={t('settings_helpManual')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                  </svg>
                </a>
              )}
              {/* Language toggle */}
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-gray-600">EN</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lang === "bn"}
                    onChange={() => setLang(lang === "en" ? "bn" : "en")}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:bg-blue-600 transition-all"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-md peer-checked:translate-x-full transform transition"></div>
                </label>
                <span className="text-sm font-medium text-gray-600">বাংলা</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex flex-col grow bg-white bg-opacity-75">
          {children}
        </main>       
      </div>
    </div>
  );
}
